import {Suspense} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import useCapitalTerms from '@di-shared/features/capital/useCapitalTerms';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {getEvent2Url} from '@di-shared/pages/[vin]/capital/event1';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient} from '@tanstack/react-query';

export interface CapitalTermsProps {
  vin: string;
}

function CapitalTerms({vin}: CapitalTermsProps) {
  return (
    <Suspense>
      <Inner vin={vin} />
    </Suspense>
  );
}

function Inner({vin}: CapitalTermsProps) {
  const queryClient = useQueryClient();
  const puid = environmentStore.getState().puid;
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {resolveError} = useApiError();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const {getFeatureAgreed} = useAvailableComponentKr({vin});
  const {financialTerms} = useCapitalTerms();

  const isFinanceManageAgree = getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT);
  const isFinanceManageAgree3rd = getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD);

  const [agreeFinanceManagement] = usePendingHandler(
    async () => {
      WebviewUtils.loading(true);
      await diApi.financialManagementController.createFinancialAgreement({
        requestBody: {
          puid,
          vin,
          featureNames: isFinanceManageAgree ? ['FINANCIAL_MANAGEMENT_3RD'] : ['FINANCIAL_MANAGEMENT', 'FINANCIAL_MANAGEMENT_3RD'],
        },
      });
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApi.vehicle.getVehicle)]});
      WebviewUtils.loading(false);

      // [2025-08-21:cy.seo] FIXME: 캐피탈 이벤트 특별 동작 - 이벤트 종료 후 제거할것 EVENT2
      if (window.location.search && window.location.search.includes('from=event')) {
        const nextScheme = `app://webView?isModalEntry=true&showNavigationBar=false&showToolbar=false&url=${encodeURIComponent(getEvent2Url(vin))}`;
        WebviewUtils.close(nextScheme);
        return;
      }
      backOrClose();
    },
    async (error) => {
      WebviewUtils.loading(false);
      const [execute] = await resolveError(error);
      execute();
    }
  );

  return (
    <>
      <main className="content page_eco_mileage">
        <section className="section section_mileage_consent">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {isFinanceManageAgree ? t('T40159') : t('T40160')}
            </h2>
            {isFinanceManageAgree && <p className="section_heading_desc">{t('T40152')}</p>}
          </div>

          <div className="consent_agree">
            <div className="divider"></div>
            {!isFinanceManageAgree &&
              financialTerms.map((terms) => (
                <div
                  key={terms.termsId}
                  className="consent_agree_item"
                >
                  <p className="consent_agree_desc">{terms.title}</p>
                  <button
                    type="button"
                    className="btn btn_ic"
                    onClick={() => navigate(ROUTE_PATHS.CAPITAL_TERMS_DETAIL.getFullPath(vin, terms.termsId))}
                  >
                    <span className="ic ic_arrow_forward_16px">
                      <span className="blind">{t('T10063')}</span>
                    </span>
                  </button>
                </div>
              ))}
            {!isFinanceManageAgree3rd && (
              <div className="consent_agree_item">
                <p className="consent_agree_desc">
                  [{t('T30308')}] {t('T40153')}
                </p>

                <button
                  type="button"
                  className="btn btn_ic"
                  onClick={() => navigate(ROUTE_PATHS.CAPITAL_TERMS_3RD_DETAIL.getFullPath(vin))}
                >
                  <span className="ic ic_arrow_forward_16px">
                    <span className="blind">{t('T10063')}</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={() => {
              emitEventLog({
                event: 'di_vehicle_finance_terms',
                data_action: isFinanceManageAgree ? 'start_reconsent' : 'agree_and_connect',
              });
              agreeFinanceManagement();
            }}
          >
            {isFinanceManageAgree ? t('T40161') : t('T40162')}
          </button>
        </div>
      </div>
    </>
  );
}

export default CapitalTerms;
